import { request } from "../../utils/request";

export type AiImageQuality = "LOW" | "HIGH" | "EXTREME";
export type AiImageModel = "GPT_IMAGE_2";

interface ApiResult<T> { code: number; data: T; msg?: string; }

export interface AiImageTemplate {
  id: number; title: string; category?: string; groupCategory?: string; badge?: string; templateType?: string;
  description?: string; tags?: string[] | string; prompt?: string; coverImageUrl?: string; aspectRatio?: string;
  resolution?: string; quality?: AiImageQuality | string; imageCount?: number; model?: AiImageModel | string;
}

export interface AiImageTask {
  taskId: number; prompt: string; referenceImageUrl?: string | null; aspectRatio?: string; resolution?: string;
  quality?: AiImageQuality | string; imageCount?: number; model?: AiImageModel | string;
  status: "success" | "failed" | "processing" | string; imageUrls?: string[]; errorCode?: string | null;
  errorMessage?: string | null; createTime?: string;
}

export interface AiImageBatch {
  batchId: number;
  status: "success" | "processing" | "failed" | string;
  imageCount: number;
  completedCount: number;
  tasks: AiImageTask[];
}

export interface AiImagePointPreviewParams {
  resolution: string;
  quality: AiImageQuality;
  imageCount: number;
}

export interface AiImagePointPreviewResult {
  estimatedPoint: number;
}

export interface AiImageGenerateResult { taskId: number; status: string; referenceImageUrl?: string | null; imageUrls?: string[]; }
export interface GenerateAiImageParams { prompt: string; aspectRatio: string; resolution: string; quality: AiImageQuality; imageCount: number; image?: File | null; }

const unwrap = <T>(result: ApiResult<T>): T => result.data;

export const getAiImageTemplatesApi = async (): Promise<AiImageTemplate[]> => unwrap(await request<ApiResult<AiImageTemplate[]>>("/ai-image/templates", { method: "GET" }));
export const getAiImagePromptStartersApi = async (): Promise<AiImageTemplate[]> => unwrap(await request<ApiResult<AiImageTemplate[]>>("/ai-image/prompt-starters", { method: "GET" }));
export const getAiImageTemplateApi = async (id: number): Promise<AiImageTemplate> => unwrap(await request<ApiResult<AiImageTemplate>>("/ai-image/templates/" + id, { method: "GET" }));
export const getAiImageTasksApi = async (params?: { status?: string; keyword?: string; pageNo?: number; pageSize?: number }): Promise<AiImageTask[]> => {
  const result = unwrap(await request<ApiResult<AiImageTask[] | AiImageTask>>("/ai-image/tasks", { method: "GET", params }));
  return Array.isArray(result) ? result : result ? [result] : [];
};
export const getAiImageTaskApi = async (taskId: number): Promise<AiImageTask> => unwrap(await request<ApiResult<AiImageTask>>("/ai-image/tasks", { method: "GET", params: { taskId } }));
export const getAiImageBatchApi = async (batchId: number): Promise<AiImageBatch> => unwrap(await request<ApiResult<AiImageBatch>>(`/ai-image/batches/${batchId}`, { method: "GET" }));
export const previewAiImagePointApi = async (params: AiImagePointPreviewParams): Promise<AiImagePointPreviewResult> => unwrap(await request<ApiResult<AiImagePointPreviewResult>>("/ai-image/point-preview", { method: "POST", body: params }));
export const deleteAiImageTaskApi = async (id: number): Promise<boolean> =>
  unwrap(await request<ApiResult<boolean>>(`/ai-image/tasks/${id}`, { method: "DELETE" }));
export const generateAiImageApi = async ({ image, ...params }: GenerateAiImageParams): Promise<AiImageGenerateResult> => {
  const body = new FormData();
  body.append("prompt", params.prompt); body.append("aspectRatio", params.aspectRatio); body.append("resolution", params.resolution);
  body.append("quality", params.quality); body.append("imageCount", String(params.imageCount)); body.append("model", "GPT_IMAGE_2");
  if (image) body.append("image", image);
  return unwrap(await request<ApiResult<AiImageGenerateResult>>("/ai-image/generate", { method: "POST", body }));
};
export const aiImageQualityToLabel = (quality?: string): string => {
  switch (String(quality || "").toUpperCase()) { case "LOW": return "标准"; case "HIGH": return "精细"; case "EXTREME": return "专业"; default: return quality || "标准"; }
};
export const aiImageLabelToQuality = (label: string): AiImageQuality => {
  if (label === "专业" || label === "EXTREME") return "EXTREME"; if (label === "精细" || label === "HIGH") return "HIGH"; return "LOW";
};
export const aiImageModelToLabel = (model?: string): string => String(model || "").toUpperCase().replace(/-/g, "_") === "GPT_IMAGE_2" ? "GPT-Image-2 (Stable)" : model || "GPT-Image-2 (Stable)";
export const parseAiImageTags = (tags?: string[] | string): string[] => {
  if (Array.isArray(tags)) return tags.filter(Boolean); if (!tags) return [];
  try { const parsed = JSON.parse(tags); return Array.isArray(parsed) ? parsed.filter((tag): tag is string => typeof tag === "string" && Boolean(tag)) : []; }
  catch { return tags.split(",").map((tag) => tag.trim()).filter(Boolean); }
};
